import { computed, ref, unref, type ComputedRef, type Ref } from 'vue';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { toast } from 'vue-sonner';
import type { Client, Project } from '~/types/models';
import { publicImmutableUploadMetadata } from '~/lib/storageUploadMetadata';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;
type ClientImageTarget = 'avatar' | 'header';

export function useClientWorkspaceMedia(options: {
    client: MaybeRef<Client | null>;
    relatedProjects: MaybeRef<Project[]>;
    canMutate: MaybeRef<boolean>;
    clientStore: any;
    tenantStore: any;
    refreshClient: () => Promise<void>;
}) {
    const avatarInput = ref<HTMLInputElement | null>(null);
    const headerInput = ref<HTMLInputElement | null>(null);
    const savingTarget = ref<ClientImageTarget | null>(null);

    const headerImage = computed(() => (
        unref(options.client)?.headerImageURL
        || unref(options.client)?.profileImageURL
        || unref(options.relatedProjects).find(project => project.profileImageURL)?.profileImageURL
        || ''
    ));
    const avatarImage = computed(() => (
        unref(options.client)?.avatarURL
        || unref(options.client)?.profileImageURL
        || ''
    ));

    function openPicker(target: ClientImageTarget) {
        if (!unref(options.canMutate) || !unref(options.client)) return;
        (target === 'avatar' ? avatarInput : headerInput).value?.click();
    }

    async function upload(file: File, target: ClientImageTarget): Promise<string> {
        const { $storage } = useNuxtApp();
        const tenantId = options.tenantStore.currentTenantId;

        if (!$storage || !tenantId) {
            throw new Error('Storage or tenant context not available.');
        }

        const filename = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const imageRef = storageRef(
            $storage,
            `tenants/${tenantId}/images/clients/${target}/${Date.now()}-${filename}`,
        );

        await options.tenantStore.ensureStorageCapacity(tenantId, file.size);
        await uploadBytes(imageRef, file, publicImmutableUploadMetadata(file));
        const imageUrl = await getDownloadURL(imageRef);
        await options.tenantStore.adjustStorageUsage(tenantId, file.size);
        return imageUrl;
    }

    async function handleSelected(event: Event, target: ClientImageTarget) {
        const input = event.target as HTMLInputElement | null;
        if (!input) return;

        const file = input.files?.[0] || null;
        if (!file) return;

        const client = unref(options.client);
        if (!client?.id) {
            input.value = '';
            return;
        }

        savingTarget.value = target;
        try {
            const imageUrl = await upload(file, target);
            await options.clientStore.updateClient(client.id, target === 'avatar'
                ? { avatarURL: imageUrl }
                : { headerImageURL: imageUrl, profileImageURL: imageUrl });
            await options.refreshClient();
            toast.success(target === 'avatar' ? 'Client avatar updated.' : 'Client header image updated.');
        } catch (error: any) {
            toast.error(
                error?.code === 'storage/unauthorized'
                    ? 'Upload blocked by Firebase Storage rules. Ask an admin to deploy storage.rules.'
                    : error?.message || (target === 'avatar'
                        ? 'Could not update client avatar.'
                        : 'Could not update client header image.'),
            );
        } finally {
            savingTarget.value = null;
            input.value = '';
        }
    }

    return {
        avatarInput,
        headerInput,
        savingTarget,
        headerImage,
        avatarImage,
        openPicker,
        handleSelected,
    };
}
